import React, { useEffect, useState } from 'react';
import Spinner from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import 'twin.macro';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'components/Resizable';
import { Sidebar } from './Sidebar';
import { Body } from './Body';
import {
  programmationModeAtom,
  useProg,
  useSetToPreviewMode,
} from 'containers/Programmations/atoms/progAtoms';
import { useHydrateAtoms } from 'jotai/react/utils';
import { useParams } from 'react-router-dom';
import AutoSubmitForm from 'components/AutoSave';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useUpdateUser from 'queries/user/useUpdateUser';
import { Form } from 'react-final-form';
import { useProgDisplayConfig } from '../atoms';
import { getView } from 'containers/Programmations/ProgV2/utils/getView';
import { PrintConfig } from './types';
import { useUserAccount } from 'queries/user/account/useUserAccount';
import { ProgrammationPDFSettings } from '@teetsh/app/src/types/programmations';

const HydrateAtoms = ({ initialValues, children }) => {
  useHydrateAtoms(initialValues);
  return children;
};

function Preview() {
  const user = useUser();

  const prog = useProg();
  const displayConfig = useProgDisplayConfig();
  const { data: userAccount } = useUserAccount();
  const updateUser = useUpdateUser();
  const { matiereId } = useParams<{ matiereId: string }>();
  const setToPreviewMode = useSetToPreviewMode();
  const matiereIds = matiereId
    ? [matiereId]
    : (prog?.matieres || []).map(m => m.id);
  const [printConfig, setPrintConfig] = useState<PrintConfig>({
    groupWeeksByPeriodes: true,
    matiereIds,
    rowTitleOrientation: 'vertical',
    preventCutting: true,
    breakBefore: false,
    view: getView({
      groupCol: displayConfig.groupCol,
      groupRow: displayConfig.groupRow,
    }),
  });

  // On synchronise la view avec le printConfig
  // car on veut permettre de changer la view
  // seulement dans la preview et pas répercuter ça
  // dans l'app
  useEffect(() => {
    setPrintConfig(v => ({
      ...v,
      view: getView({
        groupCol: displayConfig.groupCol,
        groupRow: displayConfig.groupRow,
      }),
    }));
  }, [displayConfig?.groupCol, displayConfig?.groupRow]);

  useEffect(() => {
    setToPreviewMode();
  }, []);

  return (
    <Form<ProgrammationPDFSettings>
      initialValues={user.fonts.programmation}
      onSubmit={async values => {
        await updateUser({
          ...user,
          fonts: {
            ...user.fonts,
            programmation: values,
          },
        });
        return {};
      }}
    >
      {({ handleSubmit }) => (
        <form tw="min-h-0 h-full" onSubmit={handleSubmit}>
          <AutoSubmitForm />
          <div tw="flex flex-col h-full overflow-hidden w-full">
            <ResizablePanelGroup
              direction="horizontal"
              autoSaveId="programmation-sidebar"
            >
              <Sidebar
                limitedBgSet={userAccount?.isOnOldPricing || false}
                onUpdatePrintConfig={setPrintConfig}
                printConfig={printConfig}
                matiereId={matiereId}
              />
              <ResizableHandle />
              <ResizablePanel order={1}>
                <div
                  key={JSON.stringify([
                    // Cette key est importante, sans ça le hiddenPeriodes et le hiddenDomaines ne sont pas mis à jour
                    // car il y a un rerender, mais l'url de l'iframe n'est pas mis à jour
                    displayConfig.hiddenPeriodes,
                    displayConfig.hiddenDomaines,
                  ])}
                  tw="overflow-auto h-full bg-neutral-quiet"
                >
                  <HydrateAtoms
                    initialValues={[[programmationModeAtom, 'preview']]}
                  >
                    <Body
                      hiddenPeriodes={displayConfig.hiddenPeriodes}
                      hiddenDomaines={displayConfig.hiddenDomaines}
                      printConfig={printConfig}
                    />
                  </HydrateAtoms>
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </form>
      )}
    </Form>
  );
}

// Ce wrapper c'est pour être certain que la prog est bien chargée avant de rendre le body
// Car comme on génère qu'une fois l'url de l'iframe on doit être certain que les données changent pas
// (ça concerne les data qu'on ne modifie pas dans la sidebar, car ceux-ci sont passées en paramètre à l'iframe)
export function PreviewWrapper() {
  const prog = useProg();
  if (!prog) {
    return <Spinner />;
  }
  return <Preview />;
}
